import moment, { type Moment } from "moment-timezone";
import { localized } from "../locales";
import { guardNull } from "../misc";
import type { Nullable } from "../types";
import type { DayUnit } from "../units";
import { alLocale } from "./locales/al.locale";
import { deLocale } from "./locales/de.locale";
import { enLocale } from "./locales/en.locale";
import { frLocale } from "./locales/fr.locale";
import { plLocale } from "./locales/pl.locale";
import { roLocale } from "./locales/ro.locale";
import { skLocale } from "./locales/sk.locale";
import { trLocale } from "./locales/tr.locale";

moment.updateLocale("en", enLocale);
moment.defineLocale("de", deLocale);
moment.defineLocale("pl", plLocale);
moment.defineLocale("al", alLocale);
moment.defineLocale("ro", roLocale);
moment.defineLocale("tr", trLocale);
moment.defineLocale("sk", skLocale);
moment.defineLocale("fr", frLocale);

moment.locale("de");
moment.tz.setDefault("Europe/Berlin");

type TimeFormat = "HH:mm:ss" | "HH:mm" | "HH:mm:ss.SSSSZ" | "YYYY-MM-DD HH:mm:ss.SSSSZ";

/** Extension */
declare module "moment" {
	interface Moment {
		/**
		 * THis function checks if the given date is today.
		 * @param date - The date to check.
		 * @returns True if the date is today, false otherwise.
		 */
		isToday: () => boolean;

		/**
		 * This function checks if the date is a weekend.
		 * @returns True if the date is a weekend, false otherwise.
		 */
		isWeekend: () => boolean;

		/**
		 * This function checks if the date is a Sunday.
		 * @returns True if the date is a Sunday, false otherwise.
		 */
		isSunday: () => boolean;

		/**
		 * This function returns the date in a specific format.
		 * @example "Mo, 01.01.2022"
		 * @returns
		 */
		dateFormat: () => string;

		/**
		 * This function returns the long format of the date.
		 * @example "Monday, 01.01.2022 / 12:00:00 Uhr"
		 * @returns The long format of the date.
		 */
		longFormat: () => string;

		/** This function returns the short format of the date.
		 * @example "01.01.2022 / 12:00"
		 * @returns The short format of the date.
		 */
		shortFormat: () => string;

		/**
		 * This function returns the month in a specific format.
		 * @example "June 2025"
		 * @returns The month in a specific format.
		 */
		monthFormat: () => string;

		/**
		 * This function returns a supabase timestamp.
		 * @returns The supabase timestamp.
		 */
		postgresTimestamp: () => string;

		/**
		 * This function returns a timestamp in RFC3339 Format.
		 * @see https://datatracker.ietf.org/doc/html/rfc3339
		 */
		rfc: () => string;

		/**
		 * This function returns a datestamp in ISO 8601 Format.
		 * 2022-09-27 18:00:00.000
		 * @see https://www.iso.org/iso-8601-date-and-time-format.html
		 */
		iso: () => string;

		/**
		 * This function is used to return a date-time string according to the Personio API.
		 * @example 2023-01-01T12:00:00Z
		 */
		dateTime: () => string;

		/**
		 * This function returns the data in database format.
		 * @example "2022-12-01"
		 * @example "YYYY-MM-DD"
		 * @returns The date in database format.
		 */
		db: () => string;

		/**
		 * The ISO week this day falls in, as `GGGG-WW`: the ISO week-numbering year and the ISO
		 * week. The year is the week's own, not the day's — 1 January 2027 is in week 53 of 2026,
		 * and 29 December 2025 in week 1 of 2026 — so the calendar year (`YYYY`) would name a week
		 * that does not exist, or the same number a year early.
		 * @example "2026-53"
		 * @returns The week key, read back by `getMomentByDbWeek`.
		 */
		dbWeek: () => string;

		/**
		 * This function returns the indices of the weekend.
		 * @returns The indices of the weekend. [Saturday index, Sunday index]
		 */
		getWeekendIndices: () => [number, number];

		/**
		 * Turn a week key from `dbWeek` back into a moment, parsed as `GGGG-WW` — the one parser
		 * for week keys, so the format is not repeated wherever a week is read.
		 * @param week - The week key.
		 * @returns The Monday of that ISO week.
		 */
		getMomentByDbWeek: (week: string) => moment.Moment;

		/**
		 * This function returns the previous day.
		 * @returns
		 */
		getPrevious: (unit: DayUnit) => moment.Moment;

		/**
		 *
		 * @param date This function returns a timer string between the date object and now;
		 * @returns
		 */
		getTimerString: (date: moment.Moment | Date) => `${string}:${string}:${string}`;

		/**
		 * This function calculates the delay from now to the point in ms.
		 * @param date
		 * @returns
		 */
		getDelay: (date: moment.Moment | Date | string) => number;

		/**
		 * This function is used to return the exact scheduled moment by the delay.
		 * @param date
		 * @returns
		 */
		getMomentByDelay: (date: number) => moment.Moment;

		/**
		 * This function turns a db string into a moment object or returns null if the "string" is null.
		 * @param string - The date in db format.
		 * @returns The moment object or null.
		 */
		dbStringToMoment: (string: Nullable<string>) => moment.Moment | null;

		/**
		 * This function returns an array of moment objects for the week.
		 * @example [moment("2022-01-01"), moment("2022-01-02"), ...]
		 * @returns
		 */
		yearDates: () => moment.Moment[];

		/**
		 * This function returns an array of strings for the year.
		 * @returns
		 */
		yearDatesDb: () => string[];

		/**
		 * This function returns an array of moment objects for the week.
		 * @example [moment("2022-12-01"), moment("2022-12-02"), ...]
		 * @returns The array of moment objects for the week.
		 */
		weekDates: () => moment.Moment[];

		/**
		 * This function returns an array moment object for the given week.
		 * @param startDate
		 * @param endDate
		 * @returns
		 */
		rangeDates: (startDate: Nullable<string>, endDate: Nullable<string>) => moment.Moment[];

		/**
		 * This function returns an array of strings for the given range.
		 * @param startDate
		 * @param endDate
		 * @returns
		 */
		rangeDatesDb: (startDate: Nullable<string>, endDate: Nullable<string>) => string[];

		/**
		 * This function returns an array of strings for the week.
		 * @returns
		 */
		weekDatesDb: () => string[];

		/**
		 * Calculates the days of each week in the year.
		 * @returns an array of numbers representing the days of the week in the year.
		 */
		getYearWeeksArray: () => number[];

		/**
		 * This function returns the number of days in the year.
		 * @returns The number of days in the year.
		 */
		getDaysInYear: () => number;

		/**
		 * This function returns the current time in HH:mm:ss format.
		 * @returns  The current time in HH:mm:ss format.
		 */
		getTime: (format?: TimeFormat) => string;

		/**
		 * This function returns the current time as a string with HH.mm:ss Uhr format.
		 * @returns The current time as a string.
		 */
		getTimeString: (format?: TimeFormat) => string;

		/**
		 * This function sets the time to the given time.
		 * @param time
		 * @returns
		 */
		setTime: (time: Nullable<string>) => moment.Moment;

		/**
		 * This function checks if the current date is before the given time.
		 * @param time
		 * @returns
		 */
		earliestTime: (timeA: string, timeB: string) => "first" | "second" | "equal";

		/**
		 * This function returns the date span for the current moment.
		 * @returns The date span for the current moment.
		 */
		getDateSpan: <T extends { date: string } | string>(
			items: T[],
		) => [moment.Moment, moment.Moment];

		/**
		 * This function returns the date span for the current moment as a string.
		 * @returns The date span as a string.
		 */
		getDateSpanString: <T extends { date: string } | string>(items: T[]) => string;

		/**
		 * This function moves the start and end date by a given number of days.
		 * @param startDate
		 * @param endDate
		 * @param days
		 * @returns
		 */
		moveDates: (
			startDate: Nullable<string>,
			endDate: Nullable<string>,
			days: number,
		) => {
			startDate: Nullable<string>;
			endDate: Nullable<string>;
		};

		/**
		 * This function adds a unit to the current moment.
		 * @param value
		 * @param unit
		 * @returns
		 */
		addUnit: (value: number, unit: DayUnit) => moment.Moment;
	}
}

moment.fn.isToday = function () {
	return this.isSame(moment(), "day");
};

moment.fn.isWeekend = function () {
	const day = this.day();
	const [saturdayIndex, sundayIndex] = this.getWeekendIndices();
	return day === sundayIndex || day === saturdayIndex;
};

moment.fn.isSunday = function () {
	return this.day() === this.getWeekendIndices()[1];
};

moment.fn.dateFormat = function () {
	return this.format("dd, DD.MM.YYYY");
};

moment.fn.longFormat = function () {
	return this.format("dd, DD.MM.YYYY / HH:mm:ss [Uhr]");
};

moment.fn.shortFormat = function () {
	return this.format("DD.MM.YYYY / HH:mm");
};

moment.fn.monthFormat = function () {
	return this.format("MMMM YYYY");
};

moment.fn.postgresTimestamp = function () {
	return this.utc().format("YYYY-MM-DD HH:mm:ss.SSSSZ");
};

moment.fn.rfc = function () {
	return this.format("YYYY-MM-DDTHH:mm:ss");
};

moment.fn.iso = function () {
	return this.format("YYYY-MM-DD HH:mm:ss.SSS");
};

moment.fn.dateTime = function () {
	return `${this.rfc()}Z`;
};

moment.fn.db = function () {
	return this.format("YYYY-MM-DD");
};

moment.fn.dbWeek = function () {
	return this.format("GGGG-WW");
};

moment.fn.getWeekendIndices = function () {
	const saturdayIndex = this.clone().endOf("week").day();
	const sundayIndex = saturdayIndex === 0 ? 6 : 5;
	return [saturdayIndex, sundayIndex];
};

moment.fn.getMomentByDbWeek = function (week: string) {
	return moment(week, "GGGG-WW");
};

moment.fn.getPrevious = function (unit: DayUnit) {
	switch (unit) {
		case "business-days":
			const previous1 = this.clone();
			while (previous1.isSunday()) {
				previous1.subtract(1, "days");
			}
			return previous1;
		case "work-days":
			const previous2 = this.clone().subtract(1, "days");
			while (previous2.isWeekend()) {
				previous2.subtract(1, "days");
			}
			return previous2;
		case "calendar-days":
			return this.clone().subtract(1, "days");
		case "weeks":
			return this.clone().subtract(1, "weeks");
		default:
			return this.clone().subtract(1, "days");
	}
};

moment.fn.getDelay = function (date: moment.Moment | Date | string): number {
	const target = moment(date).setTime("12:00");
	return Math.abs(this.diff(target, "milliseconds"));
};

moment.fn.getMomentByDelay = function (delay: number): moment.Moment {
	return this.add(delay, "milliseconds");
};

moment.fn.getTimerString = function (date: moment.Moment | Date) {
	const now = moment(new Date()).unix();
	const then = moment(date).unix();

	const diffSeconds = Math.abs(then - now);
	const hours = Math.floor(diffSeconds / 3600);
	const minutes = Math.floor((diffSeconds % 3600) / 60);
	const seconds = diffSeconds % 60;
	const hh = String(hours).padStart(2, "0");
	const mm = String(minutes).padStart(2, "0");
	const ss = String(seconds).padStart(2, "0");
	return `${hh}:${mm}:${ss}`;
};

moment.fn.dbStringToMoment = function (string: Nullable<string>) {
	if (!string) return null;
	if (!Number.isNaN(Number(string))) return moment(Number(string));
	return moment(string, "YYYY-MM-DD");
};

moment.fn.yearDates = function () {
	const startOfYear = this.clone().startOf("year");
	const endOfYear = this.clone().endOf("year");
	const dates: moment.Moment[] = [];

	for (let m = startOfYear; m.isBefore(endOfYear); m.add(1, "days")) {
		const date = m.clone();
		if (date.isValid()) {
			dates.push(date);
		}
	}

	return dates;
};

moment.fn.yearDatesDb = function () {
	return this.yearDates().map((date) => date.db());
};

moment.fn.rangeDates = function (startDate: Nullable<string>, endDate: Nullable<string>) {
	if (!startDate && !endDate) return [];
	if (!endDate) {
		const singleRangeItem = moment().dbStringToMoment(startDate);
		if (singleRangeItem) return [singleRangeItem];
	}

	const startItem = moment().dbStringToMoment(startDate) as moment.Moment;
	const endItem = moment().dbStringToMoment(endDate) as moment.Moment;
	const dates: moment.Moment[] = [];

	for (let m = startItem; m.isSameOrBefore(endItem); m.add(1, "days")) {
		const date = m.clone();
		if (date.isValid()) {
			dates.push(date);
		}
	}

	return dates;
};

moment.fn.rangeDatesDb = function (startDate: Nullable<string>, endDate: Nullable<string>) {
	return this.rangeDates(startDate, endDate).map((date) => date.db());
};

moment.fn.weekDates = function () {
	const startOfWeek = this.clone().startOf("week");
	const endOfWeek = this.clone().endOf("week");
	const dates: moment.Moment[] = [];

	for (let m = startOfWeek; m.isBefore(endOfWeek); m.add(1, "days")) {
		dates.push(m.clone());
	}

	return dates;
};

moment.fn.weekDatesDb = function () {
	return this.weekDates().map((date) => date.db());
};

moment.fn.getYearWeeksArray = function () {
	const year = this.year();
	const startOfYear = moment(`${year}-01-01`).startOf("week");
	const endOfYear = moment(`${year}-12-31`).endOf("week");

	const weeks: number[] = [];

	for (let m = startOfYear.clone(); m.isBefore(endOfYear); m.add(1, "week")) {
		const weekStart = m.clone();
		const weekEnd = m.clone().endOf("week");

		// Clamp to year boundaries
		const clampedStart = moment.max(weekStart, moment(`${year}-01-01`));
		const clampedEnd = moment.min(weekEnd, moment(`${year}-12-31`));

		const daysInWeek = clampedEnd.diff(clampedStart, "days") + 1;
		weeks.push(daysInWeek);
	}

	return weeks;
};

moment.fn.getDaysInYear = function () {
	const daysInYear = this.clone().endOf("year").dayOfYear();
	return daysInYear;
};

moment.fn.moveDates = function (
	startDate: Nullable<string>,
	endDate: Nullable<string>,
	days: number,
) {
	let _startDate = guardNull(startDate);
	let _endDate = guardNull(endDate);
	if (!startDate && !endDate) return { startDate: null, endDate: null };
	const startMoment = this.dbStringToMoment(startDate);
	if (startMoment) {
		startMoment.add(days, "days");
		_startDate = startMoment.db();
	}

	const endMoment = this.dbStringToMoment(endDate);
	if (endMoment) {
		endMoment.add(days, "days");
		_endDate = endMoment.db();
	}
	return { startDate: _startDate, endDate: _endDate };
};

moment.fn.getTime = function (timeFormat: TimeFormat = "HH:mm:ss") {
	return this.format(timeFormat);
};

moment.fn.getTimeString = function (timeFormat: TimeFormat = "HH:mm:ss") {
	return `${this.format(timeFormat)} ${localized("dates.time_suffix")}`;
};

moment.fn.setTime = function (time: Nullable<string>) {
	if (!time) return this;
	const [hours, minutes, seconds] = time.split(":").map(Number);
	if (Number.isNaN(hours) || Number.isNaN(minutes)) return this;
	return this.clone()
		.hours(hours || 0)
		.minutes(minutes || 0)
		.seconds(Number.isNaN(seconds) ? 0 : seconds || 0);
};

moment.fn.getDateSpan = function <T extends { date: string } | string>(items: T[]) {
	const dates =
		typeof items.at(0) === "string"
			? items.map((item) => moment(item as string))
			: items.map((item) =>
					typeof item === "object" && "date" in item ? moment(item.date) : moment(item),
				);
	const startDate = moment.min(dates);
	const endDate = moment.max(dates);
	return [startDate, endDate];
};

moment.fn.getDateSpanString = function <T extends { date: string } | string>(items: T[]) {
	const [startDate, endDate] = this.getDateSpan(items);
	if (startDate.isSame(endDate, "date")) return startDate.dateFormat();
	return `${startDate.dateFormat()} - ${endDate.dateFormat()}`;
};

moment.fn.earliestTime = function (timeA: string, timeB: string): "first" | "second" | "equal" {
	const momentA = moment(timeA, "HH:mm:ss");
	const momentB = moment(timeB, "HH:mm:ss");
	if (momentA.isBefore(momentB)) {
		return "first";
	}
	if (momentA.isAfter(momentB)) {
		return "second";
	}
	return "equal";
};

moment.fn.addUnit = function (value: number, unit: DayUnit) {
	switch (unit) {
		case "business-days":
			let businessDaysCount = 0;
			while (businessDaysCount < value) {
				this.add(1, "days");
				if (!this.isWeekend()) businessDaysCount++;
			}
			return this;
		case "work-days":
			let workDaysCount = 0;
			while (workDaysCount < value) {
				this.add(1, "days");
				if (!this.isWeekend()) workDaysCount++;
			}
			return this;
		case "calendar-days":
			return this.clone().add(value, "days");
		case "weeks":
			return this.clone().add(value, "weeks");
		default:
			return this.clone();
	}
};

export type { Moment };
export { moment };
